// Worker-event thread helpers (#4005): where a message started and how to get
// back there from the hub or the side panel.

import type { Chat, ChatMessage, RecordSourceType, TopicSource } from 'src/models/chat';

const EVENT_THREAD_KEY = /^EVENT_REPLY:(\d+):(\d+):(\d+)$/;

export function eventThreadIds(chat: Pick<Chat, 'dedupeKey'> | null | undefined) {
  const match = chat?.dedupeKey ? EVENT_THREAD_KEY.exec(chat.dedupeKey) : null;
  return match ? { companyId: Number(match[1]), eventId: Number(match[2]), workerId: Number(match[3]) } : null;
}

export const isEventThread = (chat: Pick<Chat, 'dedupeKey'> | null | undefined) => !!eventThreadIds(chat);

/** Server-assigned topic: the read decorates older messages; live pushes carry it in metadata. */
export function messageTopic(message: ChatMessage): { key: string; label: string; source?: TopicSource } | null {
  if (message.topic?.key) return message.topic;
  const topic = message.metadata?.topic as { key?: unknown; label?: unknown } | undefined;
  if (typeof topic?.key !== 'string' || typeof topic.label !== 'string') return null;
  return { key: topic.key, label: topic.label, source: message.metadata?.source as TopicSource | undefined };
}

const TOPIC_HEADINGS: Record<string, string> = {
  booking: 'Booking', callTime: 'Call Time', invoice: 'Invoice', notification: 'Request', event: 'Event'
};

/** Neutral heading for a sub-thread whose server label has not loaded; names only its kind. */
export function topicHeading(key: string): string {
  const [type, id] = key.split(':');
  if (type === 'ticket' && id) return `Ticket ${id}`;
  return TOPIC_HEADINGS[type] ?? 'Conversation';
}

/** Key the server gives a record's own sub-thread (`booking:<jobId>`, `event:<eventId>`). */
export function originTopicKey(origin: { type: RecordSourceType; id?: number }, eventId?: number | null): string {
  const id = origin.type === 'event' ? origin.id ?? eventId : origin.id;
  return `${origin.type}:${id ?? ''}`;
}

/** A ticket starts from no record, so it has nothing to open. */
export const hasSourceLink = (source?: TopicSource | null) => source?.type !== 'ticket';

/** `data-chat-focus` value of the field a topic is about, if the page renders one. */
export function sourceFocus(source?: TopicSource | null): string | null {
  if (!source) return null;
  if (source.type === 'callTime' && source.shiftId) return `callTime:${source.shiftId}`;
  if ((source.type === 'booking' || (source.type === 'notification' && source.field === 'worker')) && source.jobId) return `booking:${source.jobId}`;
  if (source.type === 'invoice' && source.jobId) return `invoice:${source.jobId}`;
  return null;
}

// Mirrors `workspaceTabValues` in the event workspace; location state wins over `?tab=`.
const WORKSPACE_TABS = { bookings: '2', 'worker-invoice': '6' } as const;

export function sourceTab(source?: TopicSource | null): keyof typeof WORKSPACE_TABS | null {
  if (!hasSourceLink(source)) return null;
  return source?.type === 'invoice' ? 'worker-invoice' : 'bookings';
}

export function sourceTabValue(source?: TopicSource | null): string | undefined {
  const tab = sourceTab(source);
  return tab ? WORKSPACE_TABS[tab] : undefined;
}

/**
 * Contract sub-page for a message source, with the side panel open on the
 * thread and the discussed field marked. Unknown sources land on Bookings;
 * tickets have no page.
 */
export function sourceLocation(chatId: number, eventId: number, topicKey?: string, source?: TopicSource | null, base?: URLSearchParams) {
  const tab = sourceTab(source);
  if (!tab) return null;
  const params = new URLSearchParams(base);
  params.set('tab', tab);
  params.set('chat', String(chatId));
  if (topicKey) params.set('chatTopic', topicKey);
  else params.delete('chatTopic');
  const focus = sourceFocus(source);
  if (focus) params.set('focus', focus);
  else params.delete('focus');
  params.delete('bookingDate');
  return { pathname: `/dashboards/jobTracker/positions/${eventId}`, search: `?${params.toString()}` };
}

export function workerName(worker?: { firstName?: string; lastName?: string } | null): string {
  return [worker?.firstName, worker?.lastName].filter(Boolean).join(' ').trim() || 'Worker';
}
